<template>
    <div>
        <b-alert :show="hasError" variant="danger">Error loading acceptance records.</b-alert>
        <b-table :items="filteredRecords" :fields="fields" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc"
            :current-page="pageNumber" :per-page="pageSize">
            <template #cell(acceptanceConfiguration.name)="data">
                <strong>
                    <RouterLink
                        :to="{ name: 'AcceptanceConfigurationDetails', params: { id: data.item.acceptanceConfiguration.id } }">
                        {{ data.value }}
                    </RouterLink>
                </strong>
            </template>
            <template #cell(signatureData)="data">
                {{ data.value ? "✅" : "❌" }}
            </template>
            <template #cell(orderId)="data">
                <strong>
                    <RouterLink :to="{ name: 'OrderDetails', params: { orderId: data.value } }">
                        {{ data.value }}
                    </RouterLink>
                </strong>
            </template>
            <template #cell(created)="data">
                {{ data.value | formatDate("LLL", false) }}
                <div>
                    <small>{{ data.item.createdBy }}</small>
                </div>
            </template>
            <template #cell(endDate)="data">
                {{ data.value | formatDate("LL", false) }}
            </template>
            <template #cell(actions)="data">
                <div class="table-icon-actions">
                    <b-button variant="link" title="Edit" @click.prevent="startEdit(data.item)">
                        <b-icon icon="pencil" variant="primary" />
                    </b-button>
                </div>
            </template>
            <template #cell(declined)="{ value }">
                <b-badge v-if="value" variant="danger">Declined</b-badge>
            </template>
        </b-table>

        <BottomPagerBar v-model="pageNumber" :total-rows="filteredRecords.length" :per-page="pageSize"
            show-page-size-selector :loading="loading" @change-page-size="pageSize = $event" />

        <b-modal :visible="!!selectedRecord" title="Acceptance Record" centered no-fade ok-title="Save"
            @hidden="selectedRecord = null" @ok="saveRecord">
            <template v-if="selectedRecord">
                <b-form-group label="Acceptance Configuration">
                    <b-input :value="selectedRecord.acceptanceConfiguration.name" readonly />
                </b-form-group>
                <b-form-group label="Has Signature Data">
                    <b-input :value="selectedRecord.signatureData ? '✅' : '❌'" readonly />
                </b-form-group>
                <b-form-group label="Order Id">
                    <b-input :value="selectedRecord.orderId" readonly />
                </b-form-group>
                <b-form-group label="End Date">
                    <el-date-picker v-model="selectedRecord.endDate" format="MM/dd/yyyy" type="date" today-button
                        reset-button :picker-options="datePickerOptions" />
                </b-form-group>
                <div>
                    <b-check v-model="selectedRecord.declined" switch>Declined</b-check>
                </div>
            </template>
        </b-modal>
    </div>
</template>

<script lang="ts">
import { AcceptanceRecord } from '@/models/AcceptanceRecord';
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import BottomPagerBar from './BottomPagerBar.vue';
import { DatePicker } from "element-ui";
// Element UI DatePickerOptions type - defined inline
interface DatePickerOptions {
  disabledDate?: (date: Date) => boolean;
}
import moment from 'moment';
import { toRaw } from 'vue';
import { BvModalEvent } from 'bootstrap-vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

@Component({
    name: "ConsultationListComponent",
    components: {
        BottomPagerBar,
        [DatePicker.name]: DatePicker,
    },
})
export default class AcceptanceRecordList extends Vue {
    @Prop({ default: null }) protected patientId!: number;

    protected records: Array<AcceptanceRecord> = [];
    protected pageNumber = 1;
    protected pageSize = 5;
    protected loading = false;
    protected hasError = false;

    protected sortBy = "created";
    protected sortDesc = true;

    protected selectedRecord: AcceptanceRecord | null = null;

    mounted() {
        this.getRecords();
    }

    @Watch("pageSize")
    @Watch("sortBy")
    @Watch("sortDesc")
    resetPageNumber() {
        this.pageNumber = 1;
    }

    @Watch("patientId")
    async getRecords() {
        if (!this.patientId) {
            this.records = [];
            return;
        }
        try {
            this.loading = true;
            this.hasError = false;
            const resp = await this.$http.get<AcceptanceRecord[]>("AcceptanceRecord", { params: { patientId: this.patientId } });
            this.records = resp.data;
        } catch (err) {
            console.error(err);
            this.hasError = true;
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.loading = false;
        }
    }

    get fields() {
        return [
            "id",
            {
                key: "acceptanceConfiguration.name",
                label: "Acceptance Configuration",
                sortable: true,
            },
            {
                key: "signatureData",
                label: "Has Signature Data",
            },
            {
                key: "orderId",
                label: "Order Id",
                sortable: true,
            },
            "endDate",
            { key: "declined" },
            {
                key: "created",
                sortable: true,
            },
            {
                key: "actions",
                label: "",
            },
        ];
    }

    get filteredRecords() {
        return this.records;
    }

    datePickerOptions: DatePickerOptions = {
        disabledDate(date: any) {
            return moment(date).isBefore(moment().subtract(1, "days"));
        },
    };

    startEdit(record: AcceptanceRecord) {
        this.selectedRecord = structuredClone(toRaw(record));
    }

    async saveRecord(e: BvModalEvent) {
        e.preventDefault();
        try {
            await this.$http.post("AcceptanceRecord", this.selectedRecord);
            await this.getRecords();
            this.selectedRecord = null;
            this.$emit("update");
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
        }
    }
}

</script>
